import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

const notionEasing = [0.16, 1, 0.3, 1];

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const panelVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.35, ease: notionEasing },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    y: 10,
    transition: { duration: 0.2, ease: 'easeIn' },
  },
};

// Group logs by month/year
const groupLogsByMonth = (logs) => {
  const groups = [];
  let currentKey = null;

  logs.forEach((log) => {
    const raw = log.dataReal || log.dataCriacao;
    const date = raw ? new Date(raw) : null;
    const key = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      : 'sem-data';
    const label = date
      ? date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
      : 'Sem data';

    if (key !== currentKey) {
      currentKey = key;
      groups.push({ key, label, logs: [] });
    }
    groups[groups.length - 1].logs.push(log);
  });

  return groups;
};

const AllLogsModal = ({ isOpen, onClose, logs, loading, error }) => {
  // ESC to close + scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  const groups = groupLogsByMonth(logs);

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.25 }}
            onClick={onClose}
          />

          {/* Centering wrapper */}
          <div
            className="fixed inset-0 z-[61] flex items-center justify-center p-4 md:p-8 pointer-events-none"
            onClick={onClose}
          >
            <motion.div
              className="w-full max-w-2xl max-h-full flex flex-col rounded-2xl border border-white/10 shadow-2xl overflow-hidden pointer-events-auto"
              style={{
                background: 'rgba(24, 24, 27, 0.92)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
              }}
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 pb-0">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-gradient">
                  #EVOLUTION-LOG
                </h2>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <p className="px-6 pt-2 text-sm text-secondary-text">
                Histórico completo de atualizações dos projetos.
              </p>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto p-6 pt-4 modal-scrollbar">
                {loading ? (
                  <div className="flex items-center gap-3 py-12 justify-center">
                    <motion.div
                      className="w-5 h-5 border-2 border-gray-600 border-t-primary rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                    <span className="text-sm text-gray-500">Carregando histórico...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                ) : logs.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">
                    Nenhum log encontrado.
                  </p>
                ) : (
                  <div className="space-y-8">
                    {groups.map((group) => (
                      <div key={group.key}>
                        {/* Month separator */}
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase whitespace-nowrap">
                            {group.label}
                          </span>
                          <div className="flex-1 border-t border-white/5" />
                        </div>

                        {/* Logs in this month */}
                        <div className="space-y-0">
                          {group.logs.map((log, i) => (
                            <div key={log._id || i} className="timeline-item">
                              {/* Date + project + tags */}
                              <div className="flex flex-col md:flex-row md:items-center mb-1.5 gap-1 md:gap-2">
                                <span className="text-xs font-semibold text-primary">
                                  {log.data ||
                                    new Date(log.dataReal || log.dataCriacao).toLocaleDateString('pt-BR')}
                                </span>
                                <span className="text-xs text-gray-400">
                                  @ {log.projeto || 'Projeto não especificado'}
                                </span>
                                {log.tags &&
                                  log.tags.map((tag, ti) => (
                                    <span
                                      key={ti}
                                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary/80"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                              </div>

                              {/* Content */}
                              {log.atualizacoes && log.atualizacoes.length > 0 ? (
                                <ul className="space-y-1 pl-1">
                                  {log.atualizacoes.map((att, ai) => (
                                    <li key={ai} className="text-sm text-gray-300">
                                      {att.tipo === 'link' ? (
                                        <a
                                          href={att.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:text-primary/80 transition-colors"
                                        >
                                          {att.texto}
                                        </a>
                                      ) : (
                                        <p>{att.texto}</p>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : log.conteudo ? (
                                <p className="text-sm text-gray-300 pl-1">{log.conteudo}</p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default AllLogsModal;
